import { Node, Edge } from "@xyflow/react";
import dagre from "dagre";
import { CompanyHierarchy } from "src/api/client-api/company/useOrganizationHierarchy";
import { CompanyHierarchyNode } from "src/api/client-api/company/useOrganizationHierarchy";

export interface CompanyNodeData extends Record<string, unknown> {
    company: CompanyHierarchy | CompanyHierarchyNode;
    name: string;
    organizationType: string;
    isRoot: boolean;
    childrenCount: number;
    isCollapsed: boolean;
    level: number;
}

export type CompanyNode = Node<CompanyNodeData>;
export type CompanyEdge = Edge;

export interface OrganizationTreeState {
    nodes: CompanyNode[];
    edges: CompanyEdge[];
    collapsedNodes: Set<string>;
}

/**
 * Construit l'arbre d'organisation à partir des données hiérarchiques optimisées de l'API
 */
export const buildOrganizationTreeFromHierarchy = (
    rootCompanies: CompanyHierarchyNode[],
    collapsedNodes: Set<string> = new Set(),
): OrganizationTreeState => {
    if (!rootCompanies || rootCompanies.length === 0) {
        return { nodes: [], edges: [], collapsedNodes };
    }

    const nodes: CompanyNode[] = [];
    const edges: CompanyEdge[] = [];
    const processedNodes = new Set<string>();

    // Fonction récursive pour traiter chaque entreprise
    const processCompany = (
        company: CompanyHierarchyNode,
        level: number = 0,
        isRoot: boolean = false,
        parentPath: string[] = [],
    ) => {
        const nodeId = `company-${company.id}`;

        // Éviter les boucles infinies dans la hiérarchie en vérifiant le chemin parental
        if (parentPath.includes(nodeId)) {
            return;
        }

        // Éviter les doublons, mais permettre le traitement si c'est à un niveau différent
        const uniqueNodeId = `${nodeId}-level-${level}`;
        if (processedNodes.has(uniqueNodeId)) return;
        processedNodes.add(uniqueNodeId);

        const isCollapsed = collapsedNodes.has(nodeId);

        // Créer le nœud seulement s'il n'existe pas déjà
        const existingNode = nodes.find((n) => n.id === nodeId);
        if (!existingNode) {
            // Gérer l'organizationType qui peut être une string ou un objet
            const organizationTypeName =
                typeof company.organizationType === "string" ?
                    "Organisation"
                :   company.organizationType?.name || "Organisation";

            const node: CompanyNode = {
                id: nodeId,
                type: "company",
                position: { x: 0, y: 0 }, // Position calculée par dagre
                data: {
                    company,
                    name: company.name,
                    organizationType: organizationTypeName,
                    isRoot,
                    childrenCount: company.children?.length || 0,
                    isCollapsed,
                    level,
                },
            };

            nodes.push(node);
        }

        // Traiter les enfants seulement si le nœud n'est pas plié
        if (!isCollapsed && company.children && company.children.length > 0) {
            company.children.forEach((child) => {
                const childNodeId = `company-${child.id}`;

                // Créer l'edge parent -> enfant seulement s'il n'existe pas déjà
                const edgeId = `edge-${nodeId}-${childNodeId}`;
                const existingEdge = edges.find((e) => e.id === edgeId);
                if (!existingEdge) {
                    edges.push({
                        id: edgeId,
                        source: nodeId,
                        target: childNodeId,
                        type: "smoothstep",
                        animated: false,
                    });
                }

                // Traiter récursivement l'enfant
                processCompany(child, level + 1, false, [...parentPath, nodeId]);
            });
        }
    };

    // Traiter toutes les entreprises racines
    rootCompanies.forEach((company) => {
        processCompany(company, 0, true);
    });

    return { nodes, edges, collapsedNodes };
};

/**
 * Déplie automatiquement tous les nœuds de la hiérarchie
 */
export const expandAllNodes = (rootCompanies: CompanyHierarchyNode[]): OrganizationTreeState => {
    return buildOrganizationTreeFromHierarchy(rootCompanies, new Set());
};

/**
 * Plie automatiquement tous les nœuds sauf les racines
 */
export const collapseAllNodes = (rootCompanies: CompanyHierarchyNode[]): OrganizationTreeState => {
    const collapsedNodeIds = new Set<string>();

    // Pour ne voir QUE les racines, il faut plier les racines elles-mêmes
    // Cela cachera tous leurs descendants (niveau 1, 2, 3, etc.)
    rootCompanies.forEach((rootCompany) => {
        collapsedNodeIds.add(`company-${rootCompany.id}`);
    });

    return buildOrganizationTreeFromHierarchy(rootCompanies, collapsedNodeIds);
};

/**
 * Toggle l'état plié/déplié d'un nœud pour les données hiérarchiques
 */
export const toggleNodeCollapseForHierarchy = (
    nodeId: string,
    rootCompanies: CompanyHierarchyNode[],
    currentCollapsedNodes: Set<string>,
): OrganizationTreeState => {
    const newCollapsedNodes = new Set(currentCollapsedNodes);

    if (newCollapsedNodes.has(nodeId)) {
        newCollapsedNodes.delete(nodeId);
    } else {
        newCollapsedNodes.add(nodeId);
    }

    return buildOrganizationTreeFromHierarchy(rootCompanies, newCollapsedNodes);
};

/**
 * Calcule les statistiques de la hiérarchie à partir des nœuds racines
 */
export const getHierarchyStatsFromNodes = (rootCompanies: CompanyHierarchyNode[]) => {
    let totalCompanies = 0;
    let maxDepth = 0;

    const calculateDepth = (company: CompanyHierarchyNode, depth: number = 0): number => {
        totalCompanies++;
        let currentMaxDepth = depth;

        // Toujours traiter les enfants pour calculer la profondeur réelle,
        // indépendamment de l'état plié/déplié
        if (company.children && company.children.length > 0) {
            company.children.forEach((child) => {
                const childDepth = calculateDepth(child, depth + 1);
                currentMaxDepth = Math.max(currentMaxDepth, childDepth);
            });
        }

        return currentMaxDepth;
    };

    rootCompanies.forEach((company) => {
        const depth = calculateDepth(company);
        maxDepth = Math.max(maxDepth, depth);
    });

    return {
        totalCompanies,
        rootCompanies: rootCompanies.length,
        maxDepth: maxDepth + 1, // +1 car on compte à partir de 0
        realMaxDepth: maxDepth + 1, // Profondeur réelle incluant tous les niveaux
    };
};

/**
 * Calcule la position des nœuds avec l'algorithme dagre (layout automatique)
 */
export const calculateLayout = (
    nodes: CompanyNode[],
    edges: CompanyEdge[],
    direction: "TB" | "LR" = "TB",
): { nodes: CompanyNode[]; edges: CompanyEdge[] } => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    // Configuration du layout
    dagreGraph.setGraph({
        rankdir: direction,
        nodesep: 50,
        ranksep: 100,
        edgesep: 10,
        marginx: 20,
        marginy: 20,
    });

    // Ajouter les nœuds
    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, {
            width: 200,
            height: 120,
        });
    });

    // Ajouter les edges
    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    // Calculer le layout
    dagre.layout(dagreGraph);

    // Appliquer les positions calculées
    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWithPosition.width / 2,
                y: nodeWithPosition.y - nodeWithPosition.height / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

/**
 * Trouve une entreprise par ID dans la hiérarchie
 */
export const findCompanyById = (companies: CompanyHierarchy[], id: number): CompanyHierarchy | null => {
    for (const company of companies) {
        if (company.id === id) return company;

        if (company.children) {
            const found = findCompanyById(company.children, id);
            if (found) return found;
        }
    }
    return null;
};

/**
 * Calcule les statistiques de la hiérarchie
 */
export const getHierarchyStats = (companies: CompanyHierarchy[]) => {
    let totalCompanies = 0;
    let maxDepth = 0;
    let rootCompanies = 0;

    const calculateDepth = (company: CompanyHierarchy, depth: number = 0): number => {
        totalCompanies++;
        if (depth === 0) rootCompanies++;

        let currentMaxDepth = depth;

        if (company.children && company.children.length > 0) {
            company.children.forEach((child) => {
                const childDepth = calculateDepth(child, depth + 1);
                currentMaxDepth = Math.max(currentMaxDepth, childDepth);
            });
        }

        return currentMaxDepth;
    };

    const roots = companies.filter((c) => !c.parents || c.parents.length === 0);
    roots.forEach((company) => {
        const depth = calculateDepth(company);
        maxDepth = Math.max(maxDepth, depth);
    });

    return {
        totalCompanies,
        rootCompanies,
        maxDepth: maxDepth + 1, // +1 car on compte à partir de 0
    };
};
